import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ExternalLink } from "lucide-react";
import hticuLogo from "@/assets/hticu-logo.png";
import { useState } from "react";
import { ApplicationDialog } from "@/components/application/ApplicationDialog";

const Footer = () => {
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);

  const handleApplyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowApplicationDialog(true);
  };

  return (
    <>
      <ApplicationDialog 
        open={showApplicationDialog} 
        onOpenChange={setShowApplicationDialog} 
      />
      
      <footer className="bg-primary text-primary-foreground mt-auto border-t border-primary-foreground/10" role="contentinfo" aria-label="Site footer">
      <div className="container mx-auto px-4 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <img 
                src={hticuLogo} 
                alt="HTICU Logo" 
                className="h-12 w-auto"
                width="48"
                height="48"
                loading="lazy"
              />
              <div>
                <div className="text-lg font-serif font-bold">HTICU</div>
                <div className="text-xs text-primary-foreground/70">Harvest Time International Christian University</div>
              </div>
            </div>
            <p className="text-sm text-primary-foreground/70">
              Equipping students with biblically sound, Christ-centered education integrating academic excellence, spiritual formation, and ministry application.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif font-semibold mb-4">Quick Links</h3>
            <nav aria-label="Quick links">
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/programs" className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth">
                    Programs
                  </Link>
                </li>
                <li>
                  <Link to="/tuition" className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth">
                    Tuition & Aid
                  </Link>
                </li>
                <li>
                  <Link to="/admissions" className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth">
                    Admissions
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth">
                    About Us
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-serif font-semibold mb-4">Resources</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a 
                  href="https://mystudentportal.hticu.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth flex items-center gap-1.5"
                >
                  Student Portal
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </li>
              <li>
                <Link to="/library" className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth">
                  Library
                </Link>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={handleApplyClick}
                  className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth cursor-pointer"
                >
                  Apply Now
                </a>
              </li>
              <li>
                <Link to="/contact" className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-serif font-semibold mb-4">Contact</h3>
            <ul className="space-y-3 text-sm" role="list">
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 mt-0.5 text-primary-foreground/70" aria-hidden="true" />
                <a 
                  href="mailto:admin@hticu.org" 
                  className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth"
                  aria-label="Email us at admin@hticu.org"
                >
                  admin@hticu.org
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 mt-0.5 text-primary-foreground/70" aria-hidden="true" />
                <a 
                  href="tel:+18773619191" 
                  className="text-primary-foreground/70 hover:text-primary-foreground transition-smooth"
                  aria-label="Call us at 877-361-9191"
                >
                  (877) 361-9191
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-primary-foreground/70" aria-hidden="true" />
                <span className="text-primary-foreground/70">
                  410 Junco Ct Suite 322, Tallahassee, FL 32304
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-primary-foreground/10 text-center text-sm text-primary-foreground/70">
          <p>
            © {new Date().getFullYear()} Harvest Time International Christian University. All rights reserved.
          </p>
          <p className="mt-2 italic text-xs">
            "The harvest truly is great, but the labourers are few." - Luke 10:2 (KJV)
          </p>
        </div>
      </div>
      </footer>
    </>
  );
};

export default Footer;
